import type { DateBasedTriggerMetadata } from '@reach/shared-types/business-automation';
import { Calendar, Undo2 } from 'lucide-react';
import React, { useContext, useState } from 'react';
import { AutomationEditorContext } from '../AutomationEditorContext';
import styles from '../EditAutomationTemplate.module.css';
import { DateTriggerConfigDialog } from './DateTriggerConfigDialog';

const { triggerStep, iconBadge, triggerUnsavedBadge, triggerUndoButton } = styles;

function getDescription(triggerMetadata: DateBasedTriggerMetadata): string {
  const { monthToTrigger, dayToTrigger } = triggerMetadata;
  if (monthToTrigger && dayToTrigger) {
    const date = new Date(2024, monthToTrigger - 1, dayToTrigger);
    const monthName = date.toLocaleDateString('en-US', { month: 'long' });
    return `Every year on ${monthName} ${dayToTrigger}`;
  }
  return 'Select a date';
}

interface DateTriggerStepProps {
  triggerMetadata: DateBasedTriggerMetadata;
}

export const DateTriggerStep: React.FC<DateTriggerStepProps> = ({ triggerMetadata }) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { state, undoTrigger } = editor;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isDirty = state.dirtySteps.trigger;
  const description = getDescription(triggerMetadata);

  return (
    <article className={triggerStep}>
      <header>
        <span className={iconBadge}>
          <Calendar />
        </span>
        <h3>
          Trigger
          {isDirty && (
            <>
              <span className={triggerUnsavedBadge}>Unsaved changes</span>
              <button
                type="button"
                onClick={undoTrigger}
                className={triggerUndoButton}
                aria-label="Undo changes"
                title="Undo changes">
                <Undo2 />
              </button>
            </>
          )}
        </h3>
      </header>
      <p>{description}</p>
      <button onClick={() => setIsDialogOpen(true)} type="button">
        Configure →
      </button>
      {isDialogOpen && (
        <DateTriggerConfigDialog
          onClose={() => setIsDialogOpen(false)}
          triggerMetadata={triggerMetadata}
        />
      )}
    </article>
  );
};
